<app-sidebar>
  <div class="data-viewer-container">
    <div class="page-nav">
      <button mat-stroked-button type="button" class="back-button" (click)="goBack()">
        <mat-icon>arrow_back</mat-icon>
        {{ backLabel }}
      </button>
    </div>

    <div class="header-section">
      <h1>Request Details</h1>
      <button
        *ngIf="isAddressLookup() && selectedAddressRow"
        mat-stroked-button
        type="button"
        (click)="closeAddressRowView()">
        Back To Results
      </button>
    </div>

    <mat-card class="request-status-card" *ngIf="waitingForRequest">
      <mat-card-content class="request-status-content">
        <div class="request-status-row">
          <mat-spinner diameter="24"></mat-spinner>
          <div>
            <p class="request-status-title">Request is {{ formatRequestStatusLabel().toLowerCase() }}</p>
            <p class="request-status-subtitle">
              We are still fetching this record. This screen refreshes automatically.
            </p>
          </div>
        </div>
      </mat-card-content>
    </mat-card>

    <mat-card
      class="request-status-card request-status-error"
      *ngIf="!loading && !waitingForRequest && (requestStatus === 'FAILED' || requestStatus === 'CANCELLED')">
      <mat-card-content>
        <div class="request-status-row">
          <mat-icon color="warn">error</mat-icon>
          <div>
            <p class="request-status-title">Request {{ formatRequestStatusLabel().toLowerCase() }}</p>
            <p class="request-status-subtitle">{{ getRequestFailureMessage() }}</p>
          </div>
        </div>
      </mat-card-content>
    </mat-card>

    <!-- Loading spinner -->
    <div *ngIf="loading" class="loading-container">
      <mat-spinner diameter="50"></mat-spinner>
      <p>Loading data...</p>
    </div>

    <mat-card class="table-card" *ngIf="!loading && !waitingForRequest && showAddressTable()">
      <mat-card-content>
        <table mat-table [dataSource]="addressRowsDataSource">
          <ng-container matColumnDef="fullName">
            <th mat-header-cell *matHeaderCellDef>Full Name</th>
            <td mat-cell *matCellDef="let row">{{ row.fullName || 'N/A' }}</td>
          </ng-container>

          <ng-container matColumnDef="houseNumber">
            <th mat-header-cell *matHeaderCellDef>House number</th>
            <td mat-cell *matCellDef="let row">{{ row.houseNumber || 'N/A' }}</td>
          </ng-container>

          <ng-container matColumnDef="street">
            <th mat-header-cell *matHeaderCellDef>Street</th>
            <td mat-cell *matCellDef="let row">{{ row.street || 'N/A' }}</td>
          </ng-container>

          <ng-container matColumnDef="erfNumber">
            <th mat-header-cell *matHeaderCellDef>Erf number</th>
            <td mat-cell *matCellDef="let row">{{ row.erfNumber || 'N/A' }}</td>
          </ng-container>

          <ng-container matColumnDef="actions">
            <th mat-header-cell *matHeaderCellDef>Actions</th>
            <td mat-cell *matCellDef="let row">
              <button mat-icon-button color="primary" type="button" (click)="viewAddressRow(row)" matTooltip="View Data">
                <mat-icon>visibility</mat-icon>
              </button>
            </td>
          </ng-container>

          <tr mat-header-row *matHeaderRowDef="addressDisplayedColumns"></tr>
          <tr mat-row *matRowDef="let row; columns: addressDisplayedColumns"></tr>
        </table>
      </mat-card-content>
    </mat-card>

    <mat-card class="profile-card" *ngIf="!loading && !waitingForRequest && hasProfileData() && (!isAddressLookup() || selectedAddressRow)">
      <mat-card-content class="profile-card-content">
        <div class="profile-top-layout">
          <div class="profile-main-column">
            <h3 class="profile-section-heading">Summary</h3>

            <div class="profile-grid">
              <div><span class="label">Full Name</span><span class="value">{{ getFullName() }}</span></div>
              <div><span class="label">ID Number</span><span class="value">{{ (selectedProfile || profile)?.idNumber || 'N/A' }}</span></div>
              <div><span class="label">Birth Date</span><span class="value">{{ (selectedProfile || profile)?.birthDate || 'N/A' }}</span></div>
              <div class="full-row" *ngIf="showSyntheticPropertyRow()">
                <span class="label">Property</span>
                <span class="value">{{ getAddressDisplayValue() }}</span>
                <button
                  *ngIf="hasLongAddress()"
                  type="button"
                  class="address-toggle-button"
                  (click)="toggleAddressExpanded()">
                  {{ addressExpanded ? 'Show less' : 'Show full address' }}
                </button>
              </div>
            </div>
          </div>

          <div class="profile-card-actions">
            <span class="last-updated-text">Last updated: {{ getLastUpdatedDisplay() }}</span>
            <div class="action-buttons-wrap">
              <button
                mat-stroked-button
                type="button"
                color="warn"
                class="profile-action-button dnc-action"
                [disabled]="dncLoading"
                (click)="markDoNotCall()">
                <mat-icon [class.spin-icon]="dncLoading">
                  {{ dncLoading ? 'autorenew' : 'phone_disabled' }}
                </mat-icon>
                <span class="action-text">
                  <span class="action-title">{{ dncLoading ? 'Applying DNC...' : 'Mark as Do Not Contact' }}</span>
                  <span class="action-subtitle">Immediately block future comms for this contact</span>
                </span>
              </button>

              <button
                mat-stroked-button
                type="button"
                color="primary"
                class="profile-action-button outdated-action"
                [disabled]="flagOutdatedLoading || flagOutdatedPending"
                (click)="flagAsOutdated()">
                <mat-icon [class.spin-icon]="flagOutdatedLoading">
                  {{ flagOutdatedLoading ? 'autorenew' : 'outlined_flag' }}
                </mat-icon>
                <span class="action-text">
                  <span class="action-title">
                    {{
                      flagOutdatedLoading
                        ? 'Submitting...'
                        : (flagOutdatedPending ? 'Already Scheduled' : 'Flag as Outdated')
                    }}
                  </span>
                  <span class="action-subtitle">
                    {{
                      flagOutdatedPending
                        ? 'Waiting for the first outdated request to be handled'
                        : 'Request a fresh lookup for newer data'
                    }}
                  </span>
                </span>
              </button>

              <button
                mat-stroked-button
                type="button"
                color="primary"
                class="profile-action-button download-action"
                [disabled]="downloadLoading"
                [matMenuTriggerFor]="recordDownloadMenu">
                <mat-icon [class.spin-icon]="downloadLoading">
                  {{ downloadLoading ? 'autorenew' : 'download' }}
                </mat-icon>
                <span class="action-text">
                  <span class="action-title">{{ downloadLoading ? 'Preparing...' : 'Download' }}</span>
                  <span class="action-subtitle">Download as PDF or Excel</span>
                </span>
              </button>
              <mat-menu #recordDownloadMenu="matMenu">
                <button mat-menu-item type="button" [disabled]="downloadLoading" (click)="downloadPdf()">
                  <mat-icon>picture_as_pdf</mat-icon>
                  <span>PDF</span>
                </button>
                <button mat-menu-item type="button" [disabled]="downloadLoading" (click)="downloadExcel()">
                  <mat-icon>table_view</mat-icon>
                  <span>Excel</span>
                </button>
              </mat-menu>
            </div>
          </div>
        </div>

        <div class="phones-section">
          <h3 class="profile-section-heading">Contact Details</h3>
          <ng-container *ngIf="(selectedProfile || profile)?.topTelephoneNumbers?.length; else noPhones">
            <div class="phone-chips">
              <span
                class="phone-pill"
                *ngFor="let phone of (selectedProfile || profile)?.topTelephoneNumbers"
                [matTooltip]="getPhoneTooltip(phone)"
                matTooltipPosition="above">
                {{ formatPhoneDisplay(phone.number) }}
              </span>
            </div>
          </ng-container>
          <ng-template #noPhones>
            <p class="phones-empty">{{ getNoPhoneMessage() }}</p>
          </ng-template>
        </div>
      </mat-card-content>
    </mat-card>

    <!-- No data message (only show when not loading and no data) -->
    <div
      *ngIf="
        !loading &&
        !waitingForRequest &&
        requestStatus === 'COMPLETE' &&
        !hasVisibleRequestData()
      "
      class="empty-state">
      <mat-icon>event_note</mat-icon>
      <p>{{ loadDataErrorMessage || 'No data available for this request.' }}</p>
    </div>
  </div>
</app-sidebar>


